import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Package, Loader2, Car, CheckCircle2, LogIn, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

export default function ScanConsume() {
  const { partId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [part, setPart] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [qty, setQty] = useState("1");
  const [vehicle, setVehicle] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  const authorized = user && user.kind !== "employee";

  const load = useCallback(async () => {
    try {
      const [p, v] = await Promise.all([api.sparePart(partId), api.sparePartVehicles()]);
      setPart(p); setVehicles(v.vehicles || []);
    } catch (e) { setErr(e?.response?.data?.detail || "تعذّر تحميل القطعة"); }
  }, [partId]);

  useEffect(() => { if (authorized) load(); }, [authorized, load]);

  const submit = async () => {
    if (!qty || Number(qty) <= 0) { toast.warning("أدخل عدد القطع"); return; }
    if (!vehicle.trim()) { toast.warning("أدخل رقم المركبة"); return; }
    setBusy(true);
    try {
      const r = await api.consumeSparePart(partId, { quantity: Number(qty), vehicle_number: vehicle.trim() });
      setDone(r); toast.success("تم تسجيل الصرف وتحديث المخزون");
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التسجيل"); }
    finally { setBusy(false); }
  };

  const reset = () => { setDone(null); setQty("1"); setVehicle(""); load(); };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-body" dir="rtl">
      <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">{children}</div>
    </div>
  );

  if (authLoading) return <Shell><Loader2 className="mx-auto h-8 w-8 animate-spin text-slate-300" /></Shell>;

  if (!authorized) {
    return (
      <Shell>
        <div className="text-center space-y-3">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-blue-50 flex items-center justify-center"><LogIn className="h-7 w-7 text-blue-600" /></div>
          <h1 className="font-heading text-xl font-bold text-slate-800">تسجيل صرف قطعة غيار</h1>
          <p className="text-sm text-slate-500">هذه الصفحة خاصة بمستخدمي الشركة. يرجى تسجيل الدخول بحساب الشركة للمتابعة.</p>
          <Button data-testid="scan-login-btn" onClick={() => { window.location.href = `/?next=/scan/${partId}`; }} className="gap-1.5 bg-blue-600 hover:bg-blue-700 w-full">
            <LogIn className="h-4 w-4" /> تسجيل الدخول
          </Button>
        </div>
      </Shell>
    );
  }

  if (err) return <Shell><p className="text-center text-rose-600" data-testid="scan-error">{err}</p></Shell>;
  if (!part) return <Shell><Loader2 className="mx-auto h-8 w-8 animate-spin text-slate-300" /></Shell>;

  if (done) {
    return (
      <Shell>
        <div className="text-center space-y-3" data-testid="scan-success">
          <div className="mx-auto h-16 w-16 rounded-full bg-emerald-50 flex items-center justify-center"><CheckCircle2 className="h-9 w-9 text-emerald-600" /></div>
          <h1 className="font-heading text-xl font-bold text-slate-800">تم تسجيل الصرف</h1>
          <p className="text-sm text-slate-600">{part.name} — الكمية المصروفة: <b className="font-mono-nums">{qty}</b></p>
          <div className={`rounded-2xl border p-3 ${done.low_stock ? "border-amber-200 bg-amber-50 text-amber-700" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
            المتبقي في المخزون: <b className="font-mono-nums text-lg">{done.remaining}</b>
            {done.low_stock && <div className="text-xs mt-1">⚠️ الكمية منخفضة — يُنصح بإعادة التوريد.</div>}
          </div>
          <Button data-testid="scan-again-btn" onClick={reset} variant="outline" className="w-full gap-1.5"><ArrowLeft className="h-4 w-4" /> تسجيل صرف آخر</Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="space-y-4" data-testid="scan-form">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-blue-50 flex items-center justify-center shrink-0"><Package className="h-6 w-6 text-blue-600" /></div>
          <div>
            <h1 className="font-heading text-lg font-bold text-slate-800">{part.name}</h1>
            <p className="text-xs text-slate-400 font-mono-nums" dir="ltr">SKU: {part.sku} · متوفر: {part.quantity}</p>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>عدد القطع المصروفة</Label>
          <Input data-testid="scan-qty" type="number" dir="ltr" value={qty} onChange={(e) => setQty(e.target.value)} className="h-12 text-lg" />
        </div>
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1"><Car className="h-4 w-4 text-slate-400" /> رقم المركبة</Label>
          <Input data-testid="scan-vehicle" list="scan-vehicles" value={vehicle} onChange={(e) => setVehicle(e.target.value)} placeholder="اختر أو اكتب رقم المركبة" className="h-12" />
          <datalist id="scan-vehicles">
            {vehicles.map((v) => <option key={v} value={v} />)}
          </datalist>
        </div>
        <Button data-testid="scan-submit-btn" onClick={submit} disabled={busy} className="w-full h-12 text-base bg-blue-600 hover:bg-blue-700 gap-1.5">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} تأكيد الصرف
        </Button>
      </div>
    </Shell>
  );
}
